import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {Object} ProductListLoadMoreRefs
 * @property {HTMLButtonElement} loadMoreButton - The button that loads the next page of products.
 */

/**
 * A custom element that fetches and appends the next page of products into a
 * product-list section's grid when its "Load more" button is clicked.
 *
 * @extends {Component<ProductListLoadMoreRefs>}
 */
class ProductListLoadMore extends Component {
  requiredRefs = ['loadMoreButton'];

  connectedCallback() {
    super.connectedCallback();
    this.currentPage = Number(this.dataset.currentPage) || 1;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  get lastPage() {
    return Number(this.dataset.lastPage) || 1;
  }

  /**
   * Fetches the next page of products for this section and appends them to the grid.
   *
   * @param {Event} event
   */
  loadMore = async (event) => {
    event.preventDefault();

    const { loadMoreButton } = this.refs;
    const grid = this.querySelector('.resource-list');

    if (!grid || loadMoreButton.disabled || this.currentPage >= this.lastPage) return;

    loadMoreButton.disabled = true;

    const nextPage = this.currentPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      const parsedPage = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = parsedPage.querySelector('.resource-list');

      if (newGrid) grid.append(...newGrid.children);

      this.currentPage = nextPage;
    } finally {
      loadMoreButton.disabled = false;
    }

    if (this.currentPage >= this.lastPage) loadMoreButton.hidden = true;
  };
}

if (!customElements.get('product-list-load-more')) {
  customElements.define('product-list-load-more', ProductListLoadMore);
}
